import * as React from 'react';
import { useState } from 'react';
import { observer } from 'mobx-react';
import { Link } from 'react-router-dom';

import styles from './loglytics_analysis.module.css';
import loglyticsStore from '../../stores/loglytics.store';
import memberStore from '../../stores/member.store';
import { LocalLoader } from '../common/loader.component';
import Copy from '../common/copy.component';
import { DiffView, MarkdownishText } from '../common/rich_text.component';

const LoglyticsAnalysis = observer(() => {
  const [expanded, setExpanded] = useState(true);
  const key = loglyticsStore.currentLoglytics?.key;
  const hasRepoMapping = memberStore.account?.githubIntegration?.repoMappings
    .some(mapping => mapping.app === memberStore.appId) ?? false;

  if (!key) return null;
  const analysis = loglyticsStore.currentLoglytics?.analysis;
  const {analysisLoading, prLoading} = loglyticsStore;

  return (
    <div className={styles.component}>
      <div className={styles.header}>
        <div className={styles.title} onClick={() => setExpanded(!expanded)}>
          AI Analysis
          {analysis && <span className={styles.toggle}>{expanded ? '▲' : '▼'}</span>}
        </div>
        <div className={styles.headerButtons}>
          {!analysisLoading && !prLoading && !!analysis?.fixes?.length && (
            analysis.prUrl ?
              <a className={styles.prLink} href={analysis.prUrl} target="_blank" rel="noopener noreferrer">View pull request</a> :
              <button className={styles.analyzeButton} onClick={() => loglyticsStore.createFixPr(key)}>Create pull request</button>)}
          {prLoading && <div className={styles.prCreating}>Opening pull request…</div>}
          {!analysisLoading &&
            <button className={styles.analyzeButton} onClick={() => loglyticsStore.runAnalysis(key, !!analysis)}>
              {analysis ? 'Re-analyze' : 'Analyze with AI'}
            </button>}
        </div>
      </div>
      {!hasRepoMapping &&
        <div className={styles.notice}>
          Analysis is based on logs only. <Link to={`/${memberStore.accountId}/preferences/code-repositories`}>Connect a code repository</Link> so
          the AI can read your source code and give a much better analysis, with concrete code fixes.
        </div>}
      {analysisLoading &&
        <div className={styles.loading}>
          <LocalLoader />
          <div>{hasRepoMapping ? 'Reading your code on GitHub and analyzing the issue…' : 'Analyzing the issue from the logs…'}</div>
        </div>}
      {!analysisLoading && loglyticsStore.analysisError &&
        <div className={styles.error}>{loglyticsStore.analysisError}</div>}
      {!analysisLoading && expanded && analysis &&
        <div className={styles.result}>
          <div className={styles.summary}>{analysis.summary}</div>
          {analysis.location &&
            <div className={styles.location}>
              Where:&nbsp;
              {analysis.location.url ?
                <a href={analysis.location.url} target="_blank" rel="noopener noreferrer">
                  {analysis.location.path}{analysis.location.line ? `:${analysis.location.line}` : ''}
                </a> :
                <span>{analysis.location.path}{analysis.location.line ? `:${analysis.location.line}` : ''}</span>}
            </div>}
          <div className={styles.sectionTitle}>Root cause</div>
          <MarkdownishText text={analysis.rootCause} />
          {analysis.suggestedFix.length > 0 &&
            <React.Fragment>
              <div className={styles.sectionTitle}>Suggested fix</div>
              <MarkdownishText text={analysis.suggestedFix} />
            </React.Fragment>}
          {analysis.fixes?.map((fix, index) => (
            <div key={index} className={styles.fix}>
              <div className={styles.fixHeader}>
                <div className={styles.fixPath}>
                  {fix.path}{fix.startLine ? `:${fix.startLine}${fix.endLine && fix.endLine !== fix.startLine ? `-${fix.endLine}` : ''}` : ''}
                </div>
                <Copy text={fix.fixedCode} />
              </div>
              <DiffView original={fix.originalCode} fixed={fix.fixedCode} />
            </div>
          ))}
        </div>}
    </div>
  );
});

export default LoglyticsAnalysis;
